import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierType,
  CarrierVisitDirection,
  OrderResponseDto,
  UpdateRailVisitCommand,
} from '@planning/app/api'
import { BaplieParseResult } from '@planning/pages/Order/stores/OrderListUploadParsingStore'
import { EditVesselVisitEstimateForm } from '@planning/pages/VesselVisit/Components/Forms/EditVesselVisitEstimateForm'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { vesselVisitService } from '@planning/services'
import railVisitService from '@planning/services/railVisitService'
import { useTranslate } from '@tolgee/react'
import { PATHS } from '@tom-ui/utils'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { OrderMovementCard } from '../../../components/OrderMovementCard'
import { ReviewUpdatesDialogViewStore } from '../Stores/ReviewUpdatesDialogViewStore'
import { OrderListUploadDialogV2 } from './Upload/OrderListUploadDialog'

interface Props {
  visitType: CarrierType
  vesselVisitItem?: IVesselVisitItem
  railVisitItem?: IRailVisitItem
  isRestow?: boolean
  direction: CarrierVisitDirection
  onViewClick: (e: any) => void
  onClickExportOrders?: (direction: CarrierVisitDirection) => void
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
}

export const OrderMovementEditableCard = observer(
  ({
    vesselVisitItem,
    railVisitItem,
    visitType,
    isRestow,
    direction,
    onViewClick,
    onClickExportOrders,
    parseBaplie,
  }: Props) => {
    const { appViewStore, nnrOrderItemStore, orderUpdateItemStore, orderListUploadViewStoreV2 } =
      usePlanningStore()
    const navigate = useNavigate()
    const { t } = useTranslate()

    const [isEditing, setIsEditing] = useState(false)
    const [isUploadOpen, setIsUploadOpen] = useState(false)

    if (!vesselVisitItem && !railVisitItem) return <></>

    const visit = vesselVisitItem ?? railVisitItem

    useEffect(() => {
      if (!orderListUploadViewStoreV2.open) {
        setIsUploadOpen(false)
      }
    }, [orderListUploadViewStoreV2.open])

    const onSubmitClicked = async (newEstimate: number) => {
      if (!visit) return

      try {
        const updatedVisit = {
          ...visit.data,
          vesselIds: visit.data.carrierIds,
        }

        if (direction === CarrierVisitDirection.Inbound)
          updatedVisit.dischargeEstimate = newEstimate
        else if (direction === CarrierVisitDirection.Outbound)
          updatedVisit.loadEstimate = newEstimate

        if (visitType === CarrierType.Train) {
          await railVisitService.put(updatedVisit as UpdateRailVisitCommand)
        } else {
          await vesselVisitService.put(updatedVisit)
          await vesselVisitService.updateVesselVisitStatus(updatedVisit.id, updatedVisit.status)
        }

        appViewStore.setShowAlert('success', t('savedSuccessfully', 'Saved successfully'))

        setIsEditing(false)
      } catch (error) {
        appViewStore.setShowAlert('error', t('failedToUpdate', 'Failed to update'))
      }
    }

    const onFileUpload = async () => {
      const visitId = visit?.id

      orderListUploadViewStoreV2.reset()

      if (visitType === CarrierType.Train) {
        await orderListUploadViewStoreV2.setRailVisitId(visitId)
      } else {
        await orderListUploadViewStoreV2.setVesselVisitId(visitId)
      }

      if (!isRestow && direction) {
        orderListUploadViewStoreV2.setDirection(direction)
      }

      setIsUploadOpen(true)
      orderListUploadViewStoreV2.setOpen(true)
    }

    const nnrOrderSummary = _.get(nnrOrderItemStore.orderSummaryForVesselVisitId, visit!.id)
    const handleAssignNnrOrder = () => {
      if (!!nnrOrderSummary && nnrOrderSummary.nnrOrders.length === 1) {
        navigate(
          `${PATHS.orders.nonNumericReleases.url}/${nnrOrderSummary.nnrOrders[0].id}/details`,
        )
      } else {
        navigate(`${PATHS.orders.nnrForVisit.url}/${visit?.id}`)
      }
    }

    const estimate =
      direction === CarrierVisitDirection.Inbound
        ? visit?.data.dischargeEstimate
        : visit?.data.loadEstimate

    const actual = isRestow
      ? vesselVisitItem?.restow?.getCount('total', direction) //TODO: check it
      : visit?.getCount('total', direction, visit.data.cargoType)

    const confirmed = visit?.getCount('confirmed', direction, visit.data.cargoType)

    const reviewUpdatesDialogStore = useMemo(
      () => new ReviewUpdatesDialogViewStore(orderUpdateItemStore),
      [orderUpdateItemStore],
    )

    useEffect(() => {
      const orders: OrderResponseDto[] = []
      if (direction === 'Inbound') {
        visit?.discharge.orders.map(i => orders.push(i.data))
      } else {
        visit?.load.orders.map(i => orders.push(i.data))
      }

      if (orders.length) {
        reviewUpdatesDialogStore.setOrders(orders)
      }
    }, [visit?.discharge.orders, visit?.load.orders, direction, reviewUpdatesDialogStore])

    return (
      (!isEditing && (
        <>
          <OrderMovementCard
            visitType={visitType}
            cargoType={visit?.data.cargoType}
            direction={!isRestow ? direction : undefined}
            estimate={estimate}
            actual={actual}
            confirmed={confirmed}
            isRestow={isRestow}
            onViewClick={onViewClick}
            onClickEdit={() => setIsEditing(true)}
            onFileUpload={onFileUpload}
            onClickExportOrders={onClickExportOrders}
            handleAssignNnrOrder={handleAssignNnrOrder}
            nnrOrderSummary={nnrOrderSummary}
            reviewUpdatesDialogStore={reviewUpdatesDialogStore}
          />

          <OrderListUploadDialogV2
            enableFieldSelectionFromOrdersView={false}
            open={isUploadOpen}
            handleDialogClose={() => {
              setIsUploadOpen(false)
              orderListUploadViewStoreV2.setOpen(false)
            }}
            visitType={visitType}
            isRestow={isRestow ?? false}
            parseBaplie={parseBaplie}
          />
        </>
      )) || (
        <EditVesselVisitEstimateForm
          direction={direction}
          estimate={estimate}
          onSubmit={onSubmitClicked}
          onCancelEdit={() => setIsEditing(false)}
        />
      )
    )
  },
)
